import { gapsHit } from "../gaps";
import type { InputFrame } from "../input";
import { surfaceAt, VERT_LAUNCH_ANGLE, type QuarterInfo } from "../level";
import type { Skater } from "../skater";
import { stickDir } from "../tricks";
import { bail } from "./bail";
import { revert } from "./ground";
import { startManual } from "./manual";
import type { SkaterEvent } from "./state";
import { GRIND_COOLDOWN, LIP_DROP_ANGLE, LIP_DROP_SPEED, LIP_MAX_ANGLE, REVERT_WINDOW } from "./tuning";

/** Rolling up quarter `q` within LIP_MAX_ANGLE of straight on (the way a lip trick needs). */
export function headingUpRamp(sk: Skater, q: QuarterInfo | undefined): boolean {
  if (!q) return false;
  const f = sk.forward;
  return Math.sign(sk.speed) * (f.x * q.axisX + f.z * q.axisZ) >= Math.cos(LIP_MAX_ANGLE);
}

/** Up on the coping of quarter `q` (the skater is at its launch line): stall there. */
export function startLip(sk: Skater, q: QuarterInfo, input: InputFrame): void {
  // Going up inside a revert window is a new line, like leaving the ground.
  if (sk.revertWindow > 0 || sk.linkWindow > 0) sk.bankCombo();
  // The stick now, or where it was on a grind pressed just before (so you
  // don't have to brake or steer all the way up the ramp to pick one).
  const now = stickDir(input.moveX, input.moveY);
  const trick = sk.tricks.lip[now === "n" && sk.grindBuffer > 0 ? sk.grindPressDir : now];
  const toCoping = q.r - q.r * Math.sin(VERT_LAUNCH_ANGLE);
  sk.x += toCoping * q.axisX;
  sk.z += toCoping * q.axisZ;
  sk.y += q.r * Math.cos(VERT_LAUNCH_ANGLE);
  sk.mode = "lip";
  sk.speed = 0;
  sk.vx = sk.vy = sk.vz = 0;
  if (sk.manual) sk.manualCarry = sk.manual.balance;
  sk.manual = null;
  sk.manualBuffer = 0;
  sk.air = null;
  sk.grindBuffer = 0;
  sk.normal = { x: 0, y: 1, z: 0 };
  sk.lip = {
    trick,
    index: sk.addTrick(trick.name, trick.points),
    balance: (sk.rng() - 0.5) * 0.3,
    balanceVel: 0,
    time: 0,
    axisX: q.axisX,
    axisZ: q.axisZ,
    r: q.r,
    held: input.held.grind,
  };
}

export function stepLip(sk: Skater, input: InputFrame, dt: number, events: SkaterEvent[]): void {
  const l = sk.lip!;
  l.time += dt;
  sk.balanceTime += dt;
  sk.combo.addPoints(l.index, l.trick.perSecond * dt);
  sk.crouch += (0.5 - sk.crouch) * Math.min(1, dt * 10);
  if (sk.stepBalance(l, input.moveX, dt, sk.wobble("lip"))) {
    bail(sk, events);
    return;
  }
  if (input.pressed.ollie || (l.held && input.released.grind)) dropIn(sk);
}

/** Puts the skater back on the ramp just below the coping (lip or bail). */
export function dropToRamp(sk: Skater): void {
  const l = sk.lip!;
  const back = l.r - l.r * Math.sin(LIP_DROP_ANGLE);
  sk.x -= back * l.axisX;
  sk.z -= back * l.axisZ;
  sk.y = surfaceAt(sk.level, sk.x, sk.z).h;
}

/**
 * Off the lip and back down the ramp: facing down it, or still facing up
 * and rolling fakie for a Rock to Fakie. Opens the revert window like a
 * ramp landing, so the stall links into a revert or a manual. A lip gap
 * scores here: the stall (on the coping) made it, and it wasn't bailed.
 */
export function dropIn(sk: Skater): void {
  const l = sk.lip!;
  const coping = { x: sk.x, z: sk.z };
  dropToRamp(sk);
  for (const g of gapsHit(sk.gaps, "lip", coping, sk)) sk.scoreGap(g);
  const upYaw = Math.atan2(l.axisX, l.axisZ);
  sk.yaw = l.trick.fakie ? upYaw : upYaw + Math.PI;
  sk.speed = l.trick.fakie ? -LIP_DROP_SPEED : LIP_DROP_SPEED;
  sk.setNormal(surfaceAt(sk.level, sk.x, sk.z));
  sk.mode = "ground";
  sk.lip = null;
  sk.grindCooldown = GRIND_COOLDOWN;
  if (sk.manualBuffer > 0) {
    startManual(sk);
    return;
  }
  sk.revertWindow = REVERT_WINDOW;
  if (sk.revertBuffer > 0) revert(sk);
}
